// Third party
import get from 'lodash/get';
import { round } from 'lodash';
import moment from 'moment';

// Correlate
import { ESGRatingClassification, ESGRatingDistributionValue } from '../enums/Esg';
import { weightedAverageForHoldings } from './calculation-utils';
import { AllocationsDictionary, formatAllocations } from './portfolio/allocation';
import PortfolioHolding from '@/types/portfolio-holding';
import Fund from '@/types/fund';
import ESGAnalytics from '@/types/esg-analytics';
import Stock from '@/types/stock';
import Portfolio from '@/types/portfolio';
import AllocationDetails from '@/types/allocation-details';

/**
 * Adjust the weight based on a coverage field
 *
 * @param {string} fieldName The name of the esg coverage field that will be used to adjust the weight
 * @param {PortfolioHolding[]} portfolioHoldings The portfolio holdings to validate
 * @param {Fund[]} funds An array containing the funds
 * @return {PortfolioHolding[]} A new list of holdings with adjusted weight
 */
export const adjustWeightForCoverage = (fieldName: string, portfolioHoldings: PortfolioHolding[], funds: Fund[]) =>
  portfolioHoldings.map(h => {
    const f = h.original.type === 'fund' && funds.find(f => h.original.id === f._id);
    let newWeight = h.weight;
    if (f && f.esg && f.esg[`${fieldName}Coverage` as keyof ESGAnalytics]) {
      const coverage = f.esg[`${fieldName}Coverage` as keyof ESGAnalytics] as number;
      newWeight = h.weight * (coverage / 100);
    }
    return {
      ...h,
      weight: newWeight,
    };
  });

/**
 * Calculate the ESG score based on multiple holdings
 *
 * @param {PortfolioHolding[]} portfolioHoldings The portfolio holdings
 * @param {{ [key: string]: number }} originalHoldingWeightMapping A mapping of the original weight by holding id
 * @param {Fund[]} funds An array containing the funds
 * @param {Stock[]} stocks An array containing the stocks
 * @return {number} The ESG score for the eligible holdings
 */
export const calculateESGScore = (
  reallocatedHoldings: PortfolioHolding[],
  originalHoldingWeightMapping: { [key: string]: number },
  funds: Fund[],
  stocks: Stock[],
): number | undefined => {
  if (reallocatedHoldings.length === 0 || Object.keys(originalHoldingWeightMapping).length === 0) {
    return undefined;
  }

  let totalRebasedScore = 0;
  let totalTrendPositive = 0;
  let totalTrendNegative = 0;
  let totalLaggard = 0;

  reallocatedHoldings.forEach(h => {
    const previousHoldingWeight = originalHoldingWeightMapping[h._id];
    const data = [...funds, ...stocks];
    if (previousHoldingWeight != null) {
      const instrument = data.find(d => d._id === h.original.id);
      if (instrument && instrument.esg && instrument.esg.rating && instrument.esg.rating.score != null) {
        const newScore = instrument.esg.rating.score * (h.weight / 100);
        totalRebasedScore += newScore;
        if (instrument.esg.trendPositiveRank != null) {
          totalTrendPositive += (previousHoldingWeight / 100) * instrument.esg.trendPositiveRank;
        }

        if (instrument.esg.trendNegativeRank != null) {
          totalTrendNegative += (previousHoldingWeight / 100) * instrument.esg.trendNegativeRank;
        }

        if (instrument.esg.laggardsRank != null) {
          totalLaggard += (previousHoldingWeight / 100) * instrument.esg.laggardsRank;
        }
      }
    }
  });

  const totalAdjustment = totalTrendPositive - totalTrendNegative - totalLaggard;
  const scoreAdjustment = totalRebasedScore * (totalAdjustment / 100);

  return totalRebasedScore + scoreAdjustment;
};

/**
 * Calculate the normalized weighted averable on a ESG field
 *
 * @param {string} fieldName The name of the field on which we want to calculate the normalized weighted average
 * @param {Portfolio} portfolio The portfolio holdings
 * @param {Fund[]} funds An array containing the funds
 * @param {Stock[]} stocks An array containing the stocks
 * @return {number} The ESG score for the eligible holdings
 */
export const calculateWeightedAvgNormalized = (fieldName: string, portfolio: Portfolio, funds: Fund[], stocks: Stock[]): number | undefined => {
  // Remove ineligible holdings
  const eligiblePortfolioHoldings = filterOutIneligibleHoldings(portfolio.holdings, [fieldName], [fieldName], funds, stocks);

  // Adjust weight based on coverage
  const adjustedWeightHoldings = adjustWeightForCoverage(fieldName, eligiblePortfolioHoldings, funds);

  // Calculate weighted average
  return weightedAverageForHoldings(adjustedWeightHoldings, [...funds, ...stocks], `esg.${fieldName}`);
};

/**
 * Remove inelibigle holdings for ESG calculation
 *
 * The holding should have the esg.{fieldName}
 * The holding should be a stock or a fund
 * If fund, it must have atleast 10 holdings
 * If fund, the esg holding date should be in the last year
 *
 * @param {PortfolioHolding[]} portfolioHoldings The portfolio holdings to validate
 * @param {string[]} fundFieldNames The name of the esg field to validate for the fund holdings
 * @param {string[]} stockFieldNames The name of the esg field to validate for the stock holdings
 * @param {Fund[]} funds An array containing the funds
 * @param {Stock[]} stocks An array containing the stocks
 * @return {PortfolioHolding[]} A new list of holdings without the ineligible holdings
 */
export const filterOutIneligibleHoldings = (
  portfolioHoldings: PortfolioHolding[],
  fundFieldNames: string[],
  stockFieldNames: string[],
  funds: Fund[],
  stocks: Stock[],
): PortfolioHolding[] => portfolioHoldings.filter(portfolioHolding => isEligibleHolding(portfolioHolding, fundFieldNames, stockFieldNames, funds, stocks));

/**
 * Get the ESG rating value based on the ESG score
 *
 * @param {number} esgScore The ESG score that will be use to retrieve the esg rating value
 * @return {ESGRatingDistributionValue} The ESG rating value
 */
export const getESGRatingValue = (esgScore: number | undefined): ESGRatingDistributionValue => {
  if (esgScore != null) {
    if (esgScore > 0 && esgScore < 1.4286) {
      return ESGRatingDistributionValue.CCC;
    }
    if (esgScore >= 1.4286 && esgScore < 2.8571) {
      return ESGRatingDistributionValue.B;
    }
    if (esgScore >= 2.8571 && esgScore < 4.2857) {
      return ESGRatingDistributionValue.BB;
    }
    if (esgScore >= 4.2857 && esgScore < 5.7143) {
      return ESGRatingDistributionValue.BBB;
    }
    if (esgScore >= 5.7143 && esgScore < 7.1429) {
      return ESGRatingDistributionValue.A;
    }
    if (esgScore >= 7.1429 && esgScore < 8.5714) {
      return ESGRatingDistributionValue.AA;
    }
    if (esgScore >= 8.5714 && esgScore <= 10) {
      return ESGRatingDistributionValue.AAA;
    }
  }
  return ESGRatingDistributionValue.NO_VALUE;
};

/**
 * Get the ESG rating classification based on the ESG rating value
 *
 * @param {ESGRatingDistributionValue} esgRatingValue The ESG rating value that will be use to retrieve the esg rating classification
 * @return {ESGRatingClassification} The ESG rating classification
 */
export const getESGRatingClassification = (esgRatingValue: ESGRatingDistributionValue | undefined): ESGRatingClassification | null => {
  if (esgRatingValue != null && esgRatingValue !== ESGRatingDistributionValue.NO_VALUE) {
    if ([ESGRatingDistributionValue.AAA, ESGRatingDistributionValue.AA].includes(esgRatingValue)) {
      return ESGRatingClassification.LEADER;
    }
    if ([ESGRatingDistributionValue.A, ESGRatingDistributionValue.BBB, ESGRatingDistributionValue.BB].includes(esgRatingValue)) {
      return ESGRatingClassification.AVERAGE;
    }
    return ESGRatingClassification.LAGGARD;
  }
  return null;
};

/**
 * Get the aggregated ESG allocations
 * @param {Portfolio} portfolio The portfolio on which we want to calculate the carbon intensity
 * @param {Fund[]} funds The array of funds object that are included as holdings
 * @param {Stock[]} stocks The array of stocks object that are included as holdings
 * @return {AllocationDetails[]} aggregated esg allocations of all the holdings
 */
export const getESGRatingAllocations = (portfolio: Portfolio, funds: Fund[], stocks: Stock[]): AllocationDetails[] => {
  const totalAllocations: AllocationsDictionary = {
    [ESGRatingDistributionValue.AAA]: {
      name: {
        'en-CA': ESGRatingDistributionValue.AAA,
        'fr-CA': ESGRatingDistributionValue.AAA,
      },
      weight: 0,
    },
    [ESGRatingDistributionValue.AA]: {
      name: {
        'en-CA': ESGRatingDistributionValue.AA,
        'fr-CA': ESGRatingDistributionValue.AA,
      },
      weight: 0,
    },
    [ESGRatingDistributionValue.A]: {
      name: {
        'en-CA': ESGRatingDistributionValue.A,
        'fr-CA': ESGRatingDistributionValue.A,
      },
      weight: 0,
    },
    [ESGRatingDistributionValue.BBB]: {
      name: {
        'en-CA': ESGRatingDistributionValue.BBB,
        'fr-CA': ESGRatingDistributionValue.BBB,
      },
      weight: 0,
    },
    [ESGRatingDistributionValue.BB]: {
      name: {
        'en-CA': ESGRatingDistributionValue.BB,
        'fr-CA': ESGRatingDistributionValue.BB,
      },
      weight: 0,
    },
    [ESGRatingDistributionValue.B]: {
      name: {
        'en-CA': ESGRatingDistributionValue.B,
        'fr-CA': ESGRatingDistributionValue.B,
      },
      weight: 0,
    },
    [ESGRatingDistributionValue.CCC]: {
      name: {
        'en-CA': ESGRatingDistributionValue.CCC,
        'fr-CA': ESGRatingDistributionValue.CCC,
      },
      weight: 0,
    },
    [ESGRatingDistributionValue.NO_VALUE]: {
      name: {
        'en-CA': ESGRatingDistributionValue.NO_VALUE,
        'fr-CA': ESGRatingDistributionValue.NO_VALUE,
      },
      weight: 0,
    },
  };

  const instruments = [...stocks, ...funds];
  portfolio.holdings.forEach(h => {
    const instrument = instruments.find(i => i._id === h.original.id);
    if (isEligibleHolding(h, ['rating.allocations', 'holdingsCount'], ['rating.allocations'], funds, stocks)) {
      instrument?.esg?.rating?.allocations?.forEach(allocation => {
        totalAllocations[allocation.name].weight = totalAllocations[allocation.name].weight + allocation.weight * (h.weight / 100);
      });
    } else {
      totalAllocations[ESGRatingDistributionValue.NO_VALUE].weight = totalAllocations[ESGRatingDistributionValue.NO_VALUE].weight + 100 * (h.weight / 100);
    }
  });

  const allocationSum = Object.values(totalAllocations).reduce((prev, curr) => prev + curr.weight, 0);
  return formatAllocations(totalAllocations, allocationSum);
};

/**
 * Get the aggregated ESG score
 * @param {Portfolio} portfolio The portfolio on which we want to calculate the carbon intensity
 * @param {Fund[]} funds The array of funds object that are included as holdings
 * @param {Stock[]} stocks The array of stocks object that are included as holdings
 * @return {number} aggregated esg score of all the eligible holdings of the portfolio
 */
export const getESGScore = (portfolio: Portfolio, funds: Fund[], stocks: Stock[]): number | undefined => {
  const originalHoldingWeightMapping: { [key: string]: number } = {};
  portfolio.holdings.forEach(h => {
    originalHoldingWeightMapping[h._id] = h.weight;
  });

  const eligiblePortfolioHoldings = filterOutIneligibleHoldings(
    portfolio.holdings,
    ['rating.score', 'holdingsCount', 'overallCoverage', 'trendPositiveRank', 'trendNegativeRank'],
    ['rating.score', 'rating.value'],
    funds,
    stocks,
  );

  // Adjust weight based on coverage
  const adjustedWeightHoldings = adjustWeightForCoverage('overall', eligiblePortfolioHoldings, funds);
  // Reallocate to 100%
  const reallocatedHoldings = reallocateWeights(adjustedWeightHoldings);

  return calculateESGScore(reallocatedHoldings, originalHoldingWeightMapping, funds, stocks);
};

/**
 * Calculate weighted average of the holdings ESG carbon intensity
 * @param {Portfolio} portfolio The portfolio on which we want to calculate the carbon intensity
 * @param {Fund[]} funds The array of funds object that are included as holdings
 * @param {Stock[]} stocks The array of stocks object that are included as holdings
 * @return {number} weighted average value of the carbon intensity
 */
export const getESGCarbonIntensity = (portfolio: Portfolio, funds: Fund[], stocks: Stock[]): number | undefined =>
  calculateWeightedAvgNormalized('carbonIntensity', portfolio, funds, stocks);

/**
 * Calculate weighted average of the holdings ESG implied temperature rise
 * @param {Portfolio} portfolio The portfolio on which we want to calculate the temperature wise
 * @param {Fund[]} funds The array of funds object that are included as holdings
 * @param {Stock[]} stocks The array of stocks object that are included as holdings
 * @return {number} weighted average value of the implied temperature
 */
export const getESGImpliedTemperatureRise = (portfolio: Portfolio, funds: Fund[], stocks: Stock[]): number | undefined =>
  calculateWeightedAvgNormalized('impliedTemperatureRise', portfolio, funds, stocks);

/**
 * Reallocate the weights back to 100%
 * @param {PortfolioHolding[]} portfolioHoldings The portfolio holdings
 * @return {PortfolioHolding[]} The holdings with weight reallocated to 100%
 */
export const reallocateWeights = (portfolioHoldings: PortfolioHolding[]): PortfolioHolding[] => {
  let denominator = 0;
  portfolioHoldings.forEach(h => {
    denominator += h.weight;
  });

  denominator /= 100;

  portfolioHoldings.forEach(ph => {
    ph.weight = round((ph.weight / 100 / denominator) * 100, 2);
  });

  return portfolioHoldings;
};

const hasMoreThan10Holdings = (portfolioHolding: PortfolioHolding, funds: Fund[]) => {
  const f = funds.find(f => f._id === portfolioHolding.original.id);
  return f && f.esg && f.esg.holdingsCount != null && f.esg.holdingsCount >= 10;
};

const isDataProvidedInLastYear = (portfolioHolding: PortfolioHolding, funds: Fund[]) => {
  const f = funds.find(f => f._id === portfolioHolding.original.id);
  return f && f.dates.esgLatestHolding && moment.utc().diff(moment(f.dates.esgLatestHolding), 'days') <= 365;
};

const isEligibleHolding = (portfolioHolding: PortfolioHolding, fundFieldNames: string[], stockFieldNames: string[], funds: Fund[], stocks: Stock[]) => {
  const isStock = portfolioHolding.original.type === 'stock';
  const isFund = portfolioHolding.original.type === 'fund';
  if (isStock) {
    return validateESGField(stockFieldNames, portfolioHolding, stocks);
  }
  if (isFund) {
    return (
      validateESGField(fundFieldNames, portfolioHolding, funds) &&
      hasMoreThan10Holdings(portfolioHolding, funds) &&
      isDataProvidedInLastYear(portfolioHolding, funds)
    );
  }
  return false;
};

const validateESGField = (fieldNames: string[], portfolioHolding: PortfolioHolding, data: (Stock | Fund)[]) => {
  const i = data.find(i => i._id === portfolioHolding.original.id);
  let isValid = true;
  fieldNames.forEach(fieldName => {
    const isValidField = get(i, `esg.${fieldName}`);
    if (isValidField == null) {
      isValid = false;
    }
  });
  return isValid;
};
